import { defineStore } from 'pinia'
import { useStore } from '../composables/store'
import * as Sentry from '@sentry/vue'
import aa from 'search-insights'
import axios from 'axios'
import { validateEmail } from '../helpers/email.js'

const store = useStore()

/** Explicit checkout flow steps (replaces overlapping local booleans like paymentUnlocked / shippingConfirmed). */
export const CheckoutFlowStep = {
	CUSTOMER: 'customer',
	SHIPPING: 'shipping',
	BILLING: 'billing',
	PAYMENT: 'payment',
}

const FLOW_STEP_RANK = {
	[CheckoutFlowStep.CUSTOMER]: 0,
	[CheckoutFlowStep.SHIPPING]: 1,
	[CheckoutFlowStep.BILLING]: 2,
	[CheckoutFlowStep.PAYMENT]: 3,
}

function isFlowStepAtLeast(flowStep, step) {
	const current = FLOW_STEP_RANK[flowStep] ?? 0
	const target = FLOW_STEP_RANK[step] ?? 0
	return current >= target
}

/** Stable hash for shipping address fields that affect BC consignment / rates */
export function hashShippingAddress(address) {
	if (!address) return ''
	return JSON.stringify({
		address1: address.address1 || '',
		address2: address.address2 || '',
		city: address.city || '',
		stateOrProvince: address.stateOrProvince || '',
		postalCode: address.postalCode || '',
		countryCode: address.countryCode || '',
		phone: (address.phone || '').replace(/\D/g, ''),
	})
}

function shippingStateDefaults() {
	return {
		addressInitialized: false,
		lastAddressHash: null,
		billingMatchesShipping: false,
		consignmentId: null,
		fetching: false,
		quoteNetworkError: false,
		newAddress: {
			mangoId: null,
		},
		saveNewShippingAddress: false,
		saving: false,
		selectedShipping: null,
		shippingAddress: null,
		shippingOptions: [],
		validated: false,
	}
}

function billingStateDefaults() {
	return {
		billingAddress: {},
		saveNewBillingAddress: false,
		saving: false,
		newAddress: {
			mangoId: null,
		},
	}
}

function paymentStateDefaults() {
	return {
		storeCredit: null,
		useStoreCredit: false,
		selectedCard: null,
		newCard: {
			name: '',
			number: '',
			expiry: '',
			cvv: '',
		},
		saveNewCard: false,
		setNewCardAsDefault: false,
		addingNewCard: false,
		cardCVV: null,
		newACH: {
			accountType: 'checking',
			accountNumber: '',
			routingNumber: '',
			ownershipType: 'personal',
			firstName: '',
			lastName: '',
			businessName: '',
		},
		addingNewACH: false,
		selectedACH: null,
		taxes: [],
	}
}

function guestStateDefaults() {
	return {
		email: '',
		emailInput: '',
		firstName: '',
		lastName: '',
		subscribe: false,
	}
}

function loadSessionShippingState() {
	try {
		const parsed = JSON.parse(window.sessionStorage.getItem('shipping') || 'null')
		if (!parsed || typeof parsed !== 'object') return shippingStateDefaults()
		return { ...shippingStateDefaults(), ...parsed }
	} catch {
		return shippingStateDefaults()
	}
}

function loadSessionBillingState() {
	try {
		const parsed = JSON.parse(window.sessionStorage.getItem('billing') || 'null')
		if (!parsed || typeof parsed !== 'object') return billingStateDefaults()
		return { ...billingStateDefaults(), ...parsed }
	} catch {
		return billingStateDefaults()
	}
}

/** Algolia accepts at most 20 objects per insights event. */
const ALGOLIA_MAX_OBJECTS_PER_EVENT = 20

/**
 * Send a purchase event for `items`, in batches Algolia will accept. `event` decides whether the
 * queryIDs travel with them, so the caller has to group the cart before calling this.
 */
function sendAlgoliaPurchase(event, items) {
	for (let i = 0; i < items.length; i += ALGOLIA_MAX_OBJECTS_PER_EVENT) {
		const batch = items.slice(i, i + ALGOLIA_MAX_OBJECTS_PER_EVENT)
		aa(event, {
			eventName: 'Products Purchased',
			index: 'InHisHands',
			objectIDs: batch.map((item) => String(item.item.id)),
			objectData: batch.map((item) => ({
				...(item.queryID ? { queryID: item.queryID } : {}),
				price: item?.variant?.currentPrice || item?.item?.currentPrice,
				quantity: item.quantity,
			})),
			currency: 'USD',
		})
	}
}

export const useCheckoutStore = defineStore('checkout', {
	state: () => ({
		algoliaQueryID: null,
		cartValidating: false,
		cartResetCount: 0,
		checkoutSuccess: false,
		loadingCheckout: false,
		showOverlay: false,
		overlayData: {
			message: 'Processing...',
			spinnerColor: 'border-t-white',
			spinnerSize: 'h-8 w-8',
		},
		applePayInstance: null,
		addresses: [],
		bcOrderId: null,
		braintreeClientToken: null,
		deviceData: null,
		orderDetails: {
			comments: '',
			isDigitalOnly: false,
		},
		guest: JSON.parse(window.sessionStorage.getItem('guest')) || {
			email: '',
			subscribe: false,
		},
		payment: {
			storeCredit: null,
			useStoreCredit: false,
			selectedCard: null,
			newCard: {
				name: '',
				number: '',
				expiry: '',
				cvv: '',
			},
			saveNewCard: false,
			setNewCardAsDefault: false,
			addingNewCard: false,
			cardCVV: null,
			newACH: {
				accountType: 'checking',
				accountNumber: '',
				routingNumber: '',
				ownershipType: 'personal',
				firstName: '',
				lastName: '',
				businessName: '',
			},
			addingNewACH: false,
			selectedACH: null,
			taxes: [],
		},
		shipping: loadSessionShippingState(),
		billing: loadSessionBillingState(),
		flowStep: CheckoutFlowStep.CUSTOMER,
	}),

	getters: {
		queryID() {
			return this.algoliaQueryID
		},
		loading() {
			return this.loadingCheckout
		},
		validatingCart() {
			return this.cartValidating
		},
		getCartResetCount() {
			return this.cartResetCount
		},
		couponType() {
			return store?.cart?.coupon?.type || ''
		},
		couponAmount() {
			const value = store?.cart?.coupon?.amount || 0
			return Number(parseFloat(value).toFixed(2))
		},
		couponDiscountSummary() {
			if (!this.couponAmount && this.couponType !== 'free_shipping') {
				return null
			}

			if (this.couponType === 'percentage_discount') {
				return `${parseFloat(this.couponAmount).toFixed(2)}% off each item`
			} else {
				let formatAmount = (amount) => {
					return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
				}

				if (this.couponType === 'per_item_discount') {
					let formattedAmount = formatAmount(this.couponAmount)
					return `${formattedAmount} off each item`
				} else if (this.couponType === 'per_total_discount') {
					let formattedAmount = formatAmount(this.couponAmount)
					return `${formattedAmount} off the order total`
				} else if (this.couponType === 'shipping_discount') {
					let formattedAmount = formatAmount(this.shippingDiscount)
					return `${formattedAmount} off the shipping total`
				} else if (this.couponType === 'free_shipping') {
					return `Free Shipping`
				}
			}
		},
		shippingTotal() {
			const value = this.shipping.selectedShipping?.cost || 0
			return Number(parseFloat(value).toFixed(2))
		},
		shippingDiscount() {
			let value
			if (this.couponType === 'free_shipping') {
				value = this.shippingTotal
			} else if (this.couponType === 'shipping_discount') {
				let discount = parseFloat(this.couponAmount)
				if (discount > this.shippingTotal) discount = this.shippingTotal
				value = discount
			} else {
				// shippingDiscount only to be used for shipping coupons.
				// Other types need to use the cart.couponDiscount returned from Mango
				value = 0
			}
			return Number(parseFloat(value).toFixed(2))
		},
		shippingText() {
			if (this.shipping.selectedShipping?.id) {
				return this.shipping.selectedShipping?.cost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
			} else {
				return 'Add Info'
			}
		},
		isShippingValidated() {
			return this.shipping.validated
		},
		showShippingDisclaimer() {
			if (this.shipping.shippingAddress?.countryCode?.length && this.shipping.shippingAddress?.countryCode !== 'US') {
				return true
			} else {
				return false
			}
		},
		isTexasShippingAddress() {
			if (!this.shipping.shippingAddress?.stateOrProvince) {
				return false
			}

			// If the shipping address has a countryCode that's not US, it's not Texas
			if (this.shipping.shippingAddress?.countryCode !== 'US') {
				return false
			}

			// Check if the state is Texas by looking at the stateOrProvince field
			if (this.shipping.shippingAddress?.stateOrProvince === 'Texas') {
				return true
			}

			return false
		},
		couponDiscount() {
			if (this.couponType === 'free_shipping' || this.couponType === 'shipping_discount') {
				return this.shippingDiscount
			} else {
				const value = store?.cart?.couponDiscount || 0
				return Number(parseFloat(value).toFixed(2))
			}
		},
		storeCredit() {
			const value = this.payment.useStoreCredit ? Math.min(store.user?.storeCreditAmounts?.[0]?.amount || 0, this.grandTotal) : 0
			return Number(parseFloat(value).toFixed(2))
		},
		grandTotal() {
			// Total cart amount, including applicable discounts, BEFORE
			// processing payment.
			// Store Credit and Gift Certificate discounts are handled as payment forms,
			// so they are not factored into the grandTotal.
			const value = (store?.cart?.total || 0) + this.shippingTotal
			return Number(parseFloat(value).toFixed(2))
		},
		giftCertificateAmount() {
			let value
			if (!store?.cart?.certificate?.balance) return 0
			if (store?.cart?.certificate?.balance <= this.grandTotal) {
				value = store.cart.certificate.balance
			} else if (store?.cart?.certificate?.balance > this.grandTotal) {
				value = this.grandTotal
			} else {
				value = 0
			}
			return Number(parseFloat(value).toFixed(2))
		},
		displayTotal() {
			const value = this.grandTotal - this.storeCredit - this.giftCertificateAmount - this.shippingDiscount
			return parseFloat(parseFloat(value).toFixed(2))
		},
		remainingBalance() {
			// This value is used as the "grand total" when processing via braintree gateway
			// Per processing order, any storeCredit or gift certificates will already have
			// been processed via the Big Commerce payment gateway
			const value = this.grandTotal - this.storeCredit - this.giftCertificateAmount - this.shippingDiscount
			return parseFloat(parseFloat(value).toFixed(2))
		},
		paymentNotRequired() {
			return this.displayTotal <= 0
		},
		isDigitalOnly() {
			return (!store.cart.cartItems.length && !!store.cart.certificates.length) || store.cart.cartItems.every((item) => item.item.type === 'digital')
		},
		emailValidated() {
			if (store?.user?.email) {
				return validateEmail(store.user.email)
			}
			return validateEmail(this.guest?.email || '')
		},
		shippingAddressValidated() {
			const shippingAddress = this.shipping.shippingAddress || {}
			const missingFields = []

			if (!shippingAddress.firstName) missingFields.push('First Name')
			if (!shippingAddress.lastName) missingFields.push('Last Name')
			if (!shippingAddress.address1) missingFields.push('Address')
			if (!shippingAddress.city) missingFields.push('City')
			if (!shippingAddress.countryCode) missingFields.push('Country')
			if (!shippingAddress.phone) missingFields.push('Phone')

			if (shippingAddress.countryCode !== 'US') {
				// Province and postal code not required for non-US addresses
				if (missingFields.length > 0) {
					return {
						value: false,
						message: `Missing shipping information: ${missingFields.join(', ')}`,
					}
				}

				return { value: true }
			}

			if (!shippingAddress.stateOrProvince) missingFields.push('State/Province')
			if (!shippingAddress.postalCode) missingFields.push('Postal Code')

			if (missingFields.length > 0) {
				return {
					value: false,
					message: `Missing shipping information: ${missingFields.join(', ')}`,
				}
			}

			return { value: true }
		},
		shippingMethodSelected() {
			return !!this.shipping.selectedShipping?.id
		},
		billingAddressValidated() {
			const billingAddress = this.billing.billingAddress || {}
			const missingFields = []

			if (!billingAddress.firstName) missingFields.push('First Name')
			if (!billingAddress.lastName) missingFields.push('Last Name')
			if (!billingAddress.address1) missingFields.push('Address')
			if (!billingAddress.city) missingFields.push('City')
			if (!billingAddress.countryCode) missingFields.push('Country')

			if (billingAddress.countryCode !== 'US') {
				// Province and postal code not required for non-US addresses
				if (missingFields.length > 0) {
					return {
						value: false,
						message: `Missing billing information: ${missingFields.join(', ')}`,
					}
				}

				return { value: true }
			}

			if (!billingAddress.stateOrProvince) missingFields.push('State/Province')
			if (!billingAddress.postalCode) missingFields.push('Postal Code')

			if (missingFields.length > 0) {
				return {
					value: false,
					message: `Missing billing information: ${missingFields.join(', ')}`,
				}
			}

			return { value: true }
		},
		expressCheckoutValidated() {
			if (!this.emailValidated) {
				return { value: false, message: 'Missing email address' }
			}

			if (!this.isDigitalOnly) {
				if (!this.shippingAddressValidated.value) {
					return { value: false, message: this.shippingAddressValidated.message }
				}

				if (!this.shippingMethodSelected) {
					return { value: false, message: 'No shipping method selected' }
				}
			}

			if (!this.billingAddressValidated.value) {
				return { value: false, message: this.billingAddressValidated.message }
			}

			return { value: true }
		},
		isShippingToLower48() {
			// show the free shipping tracker when no address has been provided
			if (!this.shipping.shippingAddress?.countryCode) return true

			// when address is provided, show the free shipping tracker if the address is in the lower 48
			return (
				this.shipping.shippingAddress?.countryCode === 'US' &&
				this.shipping.shippingAddress?.stateOrProvince !== 'Hawaii' &&
				this.shipping.shippingAddress?.stateOrProvince !== 'Alaska'
			)
		},
		/** Callable from components: `checkout.isFlowStepAtLeast(CheckoutFlowStep.PAYMENT)` */
		isFlowStepAtLeast() {
			return (step) => isFlowStepAtLeast(this.flowStep, step)
		},
		customerStepComplete() {
			if (store?.user?.id && validateEmail(store?.user?.email || '')) {
				return true
			}
			if (this.isDigitalOnly) {
				return isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.BILLING)
			}
			return isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.SHIPPING)
		},
		shippingStepComplete() {
			if (this.isDigitalOnly) return true
			return this.shippingAddressValidated.value && this.shippingMethodSelected
		},
		billingStepComplete() {
			return this.billingAddressValidated.value
		},
		/** Shipping section visible after the customer step is completed (Continue), not on valid email alone. */
		shippingSectionVisible() {
			if (this.isDigitalOnly) return false
			return isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.SHIPPING)
		},
		/** Billing unlocks only after the user has advanced past the shipping step (Continue), not on radio click alone. */
		billingSectionVisible() {
			if (!this.customerStepComplete) return false
			if (this.isDigitalOnly) return isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.BILLING)
			return isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.BILLING) && this.shippingStepComplete
		},
		/** Payment section when step is payment and all prerequisite fields are still valid. */
		showPaymentSection() {
			if (!isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.PAYMENT)) return false
			if (!this.emailValidated) return false
			if (!this.isDigitalOnly) {
				if (!this.shippingAddressValidated.value) return false
				if (!this.shippingMethodSelected) return false
			}
			return this.billingAddressValidated.value
		},
	},

	actions: {
		// Algolia Actions
		updateQueryID(id) {
			this.algoliaQueryID = id
		},

		async triggerAlgoliaPurchaseEvent() {
			return new Promise((resolve, reject) => {
				try {
					// A line with no product id has no objectID to report on, and one null in the
					// array is enough for Algolia to reject the whole batch.
					const items = (store?.cart?.cartItems || []).filter((item) => item?.item?.id)

					// purchasedObjectIDsAfterSearch requires a queryID on every object it carries.
					// Sending it for the whole cart as soon as one item had been found through a
					// search made Algolia reject the event (422), losing the attribution it was
					// meant to record. Items reached any other way go out as a plain purchase.
					sendAlgoliaPurchase('purchasedObjectIDsAfterSearch', items.filter((item) => item.queryID?.length))
					sendAlgoliaPurchase('purchasedObjectIDs', items.filter((item) => !item.queryID?.length))

					// Resolve the Promise after the event is sent
					resolve(true)
				} catch (error) {
					console.error('Error triggering Algolia purchase event:', error)
					// Reject the Promise if there's an error
					reject(error)
				}
			})
		},
		// User Actions
		updateGuestEmail(email) {
			const trimmed = typeof email === 'string' ? email.trim() : ''
			this.guest.email = trimmed && validateEmail(trimmed) ? trimmed : ''
			window.sessionStorage.setItem('guest', JSON.stringify(this.guest))
		},

		updateCartValidation(validating) {
			this.cartValidating = validating
		},

		updateCartResetCount(count) {
			this.cartResetCount = count
		},

		// Shipping Actions
		persistShippingSession() {
			window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
		},
		persistBillingSession() {
			window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
		},
		/**
		 * BC cart id changed (e.g. guest edited cart). Drop quotes/consignment only — keep addresses in session.
		 */
		onCheckoutCartChanged() {
			this.invalidateShippingConsignment()
			this.shipping.lastAddressHash = null
			this.updateShippingValidation(false)
			if (!this.isDigitalOnly && isFlowStepAtLeast(this.flowStep, CheckoutFlowStep.BILLING)) {
				this.flowStep = CheckoutFlowStep.SHIPPING
			}
			if (this.shipping.billingMatchesShipping && this.shipping.shippingAddress?.address1) {
				this.copyShippingToBilling()
			}
			this.persistShippingSession()
		},
		resetShipping() {
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'resetShipping called',
				data: {
					hadAddress: !!this.shipping.shippingAddress,
					hadMethod: !!this.shipping.selectedShipping,
					optionsCount: this.shipping.shippingOptions?.length || 0,
				},
				level: 'info',
			})
			this.shipping.shippingOptions = []
			this.shipping.selectedShipping = null
			this.shipping.consignmentId = null
			this.shipping.lastAddressHash = null
			this.shipping.billingMatchesShipping = false
			this.persistShippingSession()
		},
		resetShippingAddress() {
			const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown'
			const previousAddress = this.shipping.shippingAddress
			console.log('[checkout-store] resetShippingAddress', { previousAddress, caller })
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'resetShippingAddress called',
				data: {
					hadAddress: !!previousAddress,
					previousFirstName: previousAddress?.firstName || null,
					previousCity: previousAddress?.city || null,
					caller,
				},
				level: 'warning',
			})
			this.shipping.selectedShipping = null
			this.shipping.shippingOptions = []
			this.shipping.shippingAddress = null
			this.shipping.consignmentId = null
			this.shipping.lastAddressHash = null
			this.shipping.billingMatchesShipping = false
			this.persistShippingSession()
			try {
				window.sessionStorage.removeItem('billing')
			} catch {
				// ignore storage errors
			}
		},
		updateShippingOptions(options) {
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'updateShippingOptions called',
				data: { count: options?.length || 0 },
				level: 'info',
			})
			this.shipping.shippingOptions = options
		},
		updateShippingValidation(value) {
			this.shipping.validated = value
		},
		selectShipping(shipping) {
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'selectShipping called',
				data: {
					methodId: shipping?.id || null,
					description: shipping?.description || null,
					cost: shipping?.cost ?? null,
					hadAddress: !!this.shipping.shippingAddress,
				},
				level: 'info',
			})
			this.shipping.selectedShipping = shipping
			window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
		},
		setDefaultShippingAddress() {
			// Keep in-checkout address (e.g. guest entry) when loading the saved address book after login
			if (this.shipping.shippingAddress?.address1) {
				if (!this.shipping.shippingAddress.email) {
					this.shipping.shippingAddress.email = this.store?.user?.email || this.guest.email
				}
				window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
				return
			}

			let defaultAddress = this.addresses.find((address) => address.isDefault === true)
			if (!defaultAddress && this.addresses.length > 0) {
				defaultAddress = this.addresses[this.addresses.length - 1]
			}
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'setDefaultShippingAddress called',
				data: {
					foundDefault: !!defaultAddress,
					addressCount: this.addresses?.length || 0,
					firstName: defaultAddress?.firstName || null,
					city: defaultAddress?.city || null,
				},
				level: 'info',
			})
			this.shipping.shippingAddress = defaultAddress || null
			if (this.shipping.shippingAddress?.address1) {
				this.shipping.shippingAddress.email = this.store?.user?.email || this.guest.email
			}
			window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
		},
		setDefaultShippingMethod() {
			const method = this.shipping.shippingOptions[0]
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'setDefaultShippingMethod called',
				data: {
					methodId: method?.id || null,
					description: method?.description || null,
					optionsCount: this.shipping.shippingOptions?.length || 0,
				},
				level: 'info',
			})
			this.shipping.selectedShipping = method
			window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
		},
		/** Clears BC consignment + selected method when the committed address changes */
		invalidateShippingConsignment() {
			const previousMethodId = this.shipping.selectedShipping?.id || null
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'invalidateShippingConsignment',
				data: { previousMethodId, consignmentId: this.shipping.consignmentId || null },
				level: 'info',
			})
			this.shipping.selectedShipping = null
			this.shipping.consignmentId = null
			this.shipping.shippingOptions = []
			this.persistShippingSession()
			return { previousMethodId }
		},
		/**
		 * Commit shipping address to Pinia (single source of truth).
		 * When the address hash changes after a shipping method was selected, invalidates consignment.
		 * @returns {{ addressChanged: boolean, previousMethodId: string|null, needsShippingRefetch: boolean }}
		 */
		commitShippingAddress(address) {
			const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown'
			const cloned = address ? JSON.parse(JSON.stringify(address)) : null
			const newHash = hashShippingAddress(cloned)
			const oldHash = this.shipping.lastAddressHash
			const addressChanged = oldHash !== null && newHash !== oldHash
			const hadShippingSelection = !!(this.shipping.selectedShipping?.id || this.shipping.consignmentId)

			let previousMethodId = null
			if (addressChanged && hadShippingSelection) {
				;({ previousMethodId } = this.invalidateShippingConsignment())
				this.updateShippingValidation(false)
			}

			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'commitShippingAddress',
				data: {
					addressChanged,
					hadShippingSelection,
					previousMethodId,
					firstName: cloned?.firstName || null,
					city: cloned?.city || null,
					caller,
				},
				level: 'info',
			})

			if (cloned) {
				this.shipping.shippingAddress = cloned
				this.shipping.shippingAddress.email = store?.user?.email || this.guest.email
			} else {
				this.shipping.shippingAddress = null
			}

			this.shipping.lastAddressHash = newHash || null
			this.persistShippingSession()

			const needsShippingRefetch =
				!!cloned?.address1 &&
				(addressChanged || (oldHash === null && !this.shipping.shippingOptions?.length && !this.shipping.consignmentId))

			return { addressChanged, previousMethodId, needsShippingRefetch }
		},
		setShippingAddress(address) {
			return this.commitShippingAddress(address)
		},
		setShippingOptions(options) {
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'setShippingOptions called',
				data: {
					count: options?.length || 0,
					hadAddress: !!this.shipping.shippingAddress,
					hadMethod: !!this.shipping.selectedShipping,
				},
				level: 'info',
			})
			this.shipping.shippingOptions = options
			if (options?.length) this.shipping.quoteNetworkError = false
			this.persistShippingSession()
		},
		updateConsignmentId(id) {
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'updateConsignmentId called',
				data: {
					consignmentId: id || null,
					hadAddress: !!this.shipping.shippingAddress,
					hadMethod: !!this.shipping.selectedShipping,
				},
				level: 'info',
			})
			this.shipping.consignmentId = id
			this.persistShippingSession()
		},
		updateAddressInitialized(value) {
			this.shipping.addressInitialized = value
		},
		setLastAddressHash(hash) {
			this.shipping.lastAddressHash = hash
		},
		setFetchingShippingStatus(isFetching) {
			this.shipping.fetching = isFetching
		},
		setShippingQuoteNetworkError(value) {
			this.shipping.quoteNetworkError = !!value
		},

		// Billing Actions
		initializeBillingAddresses(addresses) {
			this.addresses = addresses
			window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
			this.setDefaultBillingAddress()
		},
		setDefaultBillingAddress() {
			if (this.shipping.billingMatchesShipping && this.shipping.shippingAddress?.address1) {
				this.copyShippingToBilling()
				return
			}

			if (this.billing.billingAddress?.address1) {
				if (!this.billing.billingAddress.email) {
					this.billing.billingAddress.email = this.store?.user?.email || this.guest.email
				}
				window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
				return
			}

			let defaultAddress = this.addresses.find((address) => address.isDefault === true)
			if (!defaultAddress && this.addresses.length > 0) {
				defaultAddress = this.addresses[this.addresses.length - 1]
			}
			this.billing.billingAddress = defaultAddress || null
			if (this.billing.billingAddress?.address1) {
				this.billing.billingAddress.email = this.store?.user?.email || this.guest.email
			}
			window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
		},
		setBillingEmail(email) {
			this.billing.billingAddress.email = email
			window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
		},
		setBillingAddress(address) {
			this.billing.billingAddress = address
			this.billing.billingAddress.email = this.store?.user?.email || this.guest.email
			window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
		},
		updateBillingMatchesShipping(value) {
			this.shipping.billingMatchesShipping = value
			this.persistShippingSession()
		},
		copyShippingToBilling() {
			Sentry.addBreadcrumb({
				category: 'checkout.shipping',
				message: 'copyShippingToBilling called',
				data: {
					hasShippingAddress: !!this.shipping.shippingAddress,
					shippingCity: this.shipping.shippingAddress?.city || null,
					hadMethod: !!this.shipping.selectedShipping,
				},
				level: 'info',
			})
			this.billing.billingAddress = JSON.parse(JSON.stringify(this.shipping.shippingAddress))
			this.persistBillingSession()
		},
		resetBillingAddress() {
			this.billing.billingAddress = null
			window.sessionStorage.removeItem('billing')
		},

		setFlowStep(step) {
			if (!FLOW_STEP_RANK[step]) return
			this.flowStep = step
		},
		advanceFlowStep(step) {
			if (!FLOW_STEP_RANK[step]) return
			const current = FLOW_STEP_RANK[this.flowStep] ?? 0
			const target = FLOW_STEP_RANK[step] ?? 0
			if (target > current) this.flowStep = step
		},
		/**
		 * Derive flow step from persisted Pinia state when the user returns mid-checkout.
		 */
		restoreFlowStepFromStore() {
			if (!this.emailValidated) {
				this.flowStep = CheckoutFlowStep.CUSTOMER
				return
			}
			if (this.isDigitalOnly) {
				this.flowStep = this.billingAddressValidated.value ? CheckoutFlowStep.PAYMENT : CheckoutFlowStep.BILLING
				return
			}
			if (!this.shippingStepComplete) {
				this.flowStep = CheckoutFlowStep.SHIPPING
				return
			}
			if (!this.billingAddressValidated.value) {
				this.flowStep = CheckoutFlowStep.BILLING
				return
			}
			this.flowStep = CheckoutFlowStep.PAYMENT
		},

		/**
		 * Clear all checkout state tied to the current user/session (logout or login-as-new-user).
		 * Does not clear cart line items in localStorage — only checkout Pinia + sessionStorage.
		 */
		resetCheckoutForLogout() {
			Sentry.addBreadcrumb({
				category: 'checkout',
				message: 'resetCheckoutForLogout',
				level: 'info',
			})

			this.guest = guestStateDefaults()
			this.addresses = []
			this.shipping = shippingStateDefaults()
			this.billing = billingStateDefaults()
			this.payment = paymentStateDefaults()
			this.orderDetails.comments = ''
			this.bcOrderId = null
			this.braintreeClientToken = null
			this.deviceData = null
			this.applePayInstance = null
			this.algoliaQueryID = null
			this.checkoutSuccess = false
			this.flowStep = CheckoutFlowStep.CUSTOMER

			try {
				window.sessionStorage.removeItem('guest')
				window.sessionStorage.removeItem('shipping')
				window.sessionStorage.removeItem('billing')
				window.sessionStorage.removeItem('addresses')
				window.sessionStorage.removeItem('checkoutInitBcId')
			} catch {
				// ignore storage errors
			}
		},

		/**
		 * Guest → login on /checkout: clear payment and BC session artifacts only.
		 * Keeps shipping/billing the guest entered (and flow step) so initializeAddresses
		 * does not fall through to the account default address.
		 */
		resetCheckoutForLoginPreserveProgress() {
			Sentry.addBreadcrumb({
				category: 'checkout',
				message: 'resetCheckoutForLoginPreserveProgress',
				level: 'info',
			})

			this.payment = paymentStateDefaults()
			this.addresses = []
			this.bcOrderId = null
			this.braintreeClientToken = null
			this.deviceData = null
			this.applePayInstance = null
			this.algoliaQueryID = null
			this.checkoutSuccess = false

			// New logged-in BC cart — refetch quotes for the same physical address
			this.shipping.selectedShipping = null
			this.shipping.shippingOptions = []
			this.shipping.consignmentId = null
			this.persistShippingSession()

			this.updateLoading(true)

			try {
				window.sessionStorage.removeItem('checkoutInitBcId')
				window.sessionStorage.removeItem('addresses')
			} catch {
				// ignore storage errors
			}
		},

		// Payment Actions
		async fetchStoreCredit() {
			if (!store?.user?.bcId) return
			try {
				const token = window.localStorage.getItem('token')
				const headers = token && token !== 'undefined' && token !== 'null' ? { Authorization: token } : {}
				let response = await axios.get(`${store.api}/controllers/bigCommerce/checkout/payments/store_credit/${store.user.bcId}`, {
					headers,
					withCredentials: true,
				})
				if (response?.data?.success) {
					this.payment.storeCredit = response?.data?.storeCredit
				}
			} catch (error) {
				console.error('Error fetching store credit:', error)
			}
		},
		updateUseStoreCredit(value) {
			this.payment.useStoreCredit = value
		},
		selectCard(card) {
			this.payment.selectedCard = card
		},
		setNewCard(card) {
			this.payment.newCard = card
		},
		updateSaveNewCard(value) {
			this.payment.saveNewCard = value
		},
		updateSetNewCardAsDefault(value) {
			this.payment.setNewCardAsDefault = value
		},
		setCardCVV(value) {
			this.payment.cardCVV = value
		},
		updateAddingNewCard(value) {
			this.payment.addingNewCard = value
		},
		selectACH(account) {
			this.payment.selectedACH = account
		},
		setNewACH(account) {
			this.payment.newACH = account
		},
		updateAddingNewACH(value) {
			this.payment.addingNewACH = value
		},
		updateTaxes(taxes) {
			this.payment.taxes = taxes
		},
		setBcOrderId(id) {
			this.bcOrderId = id
		},

		// General Actions
		updateLoading(value) {
			this.loadingCheckout = value
		},
		setAddresses(addresses) {
			this.addresses = addresses
			window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
		},
		addressesPush(address) {
			this.addresses.push(address)
			window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
		},
		initializeAddresses(addresses) {
			this.addresses = addresses
			window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
			this.setDefaultShippingAddress()
			this.setDefaultBillingAddress()
		},
		clearAddresses() {
			this.addresses = []
			window.sessionStorage.removeItem('addresses')
		},
		setOrderComments(comments) {
			this.orderDetails.comments = comments
		},
		resetGuest() {
			this.guest = guestStateDefaults()
			window.sessionStorage.setItem('guest', JSON.stringify(this.guest))
		},

		// Overlay Actions
		updateShowOverlay(value) {
			this.showOverlay = value
		},
		updateOverlayData(data) {
			this.overlayData = data
		},

		// Cart LocalStorage Actions
		saveCartToLocalStorage(cart) {
			const now = new Date().toISOString()
			if (!cart.created) {
				cart.created = now
			}
			cart.updated = now
			window.localStorage.setItem('cart', JSON.stringify(cart))
		},
		getCartFromLocalStorage() {
			const cartString = window.localStorage.getItem('cart')
			console.log('🛒 getCartFromLocalStorage - cartString exists:', !!cartString)
			if (!cartString) return null

			const cart = JSON.parse(cartString)
			const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
			const cartUpdatedDate = cart.updated ? new Date(cart.updated) : null

			console.log('🛒 Cart updated:', cart.updated)
			console.log('🛒 Cart updated as Date:', cartUpdatedDate)
			console.log('🛒 30 days ago:', thirtyDaysAgo)
			console.log('🛒 Is cart older than 30 days:', cartUpdatedDate && cartUpdatedDate < thirtyDaysAgo)

			// Legacy guest carts may not have created/updated fields - set them now so they expire in 30 days
			if (!cart.updated || !cart.created) {
				const now = new Date().toISOString()
				if (!cart.created) cart.created = now
				if (!cart.updated) cart.updated = now
				window.localStorage.setItem('cart', JSON.stringify(cart))
			}

			if (new Date(cart.updated) < thirtyDaysAgo) {
				console.log('🗑️ Cart is stale, removing from localStorage')
				window.localStorage.removeItem('cart')
				return null
			}

			console.log('✅ Cart is valid, returning cart with', cart.cartItems?.length, 'items')
			return cart
		},
	},
})
